"use client";
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useEffect,
} from "react";

export type PushPayloadData = {
  chat_id?: string;
  msg?: string;
  title?: string;
  url?: string;
  user_id?: string;
  [key: string]: any;
};

type Listener = (data: PushPayloadData) => void;

type PushNotificationContextValue = {
  emitPushEvent: (data: PushPayloadData) => void;
  subscribe: (listener: Listener) => () => void;
};

const PushNotificationContext =
  createContext<PushNotificationContextValue | null>(null);

export function PushNotificationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const listenersRef = useRef(new Set<Listener>());

  const emitPushEvent = useCallback((data: PushPayloadData) => {
    listenersRef.current.forEach((listener) => {
      try {
        listener(data);
      } catch (err) {
        console.error("Push event listener error", err);
      }
    });
  }, []);

  const subscribe = useCallback((listener: Listener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  return (
    <PushNotificationContext.Provider value={{ emitPushEvent, subscribe }}>
      {children}
    </PushNotificationContext.Provider>
  );
}

export function usePushNotificationsEmitter() {
  const ctx = useContext(PushNotificationContext);
  if (!ctx)
    throw new Error(
      "usePushNotificationsEmitter must be used within PushNotificationProvider"
    );
  return ctx.emitPushEvent;
}

export function usePushNotifications() {
  const ctx = useContext(PushNotificationContext);
  if (!ctx)
    throw new Error(
      "usePushNotifications must be used within PushNotificationProvider"
    );
  const { subscribe } = ctx;
  const onPushEvent = useCallback(
    (listener: Listener) => subscribe(listener),
    [subscribe]
  );
  return { onPushEvent };
}
